import { useLayoutEffect, type ReactElement } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ChildrenProvider } from "./lib/childrenStore";
import { AudioPlayerProvider } from "./lib/audioPlayer";
import { isDemoMode } from "./lib/dataSource";
import { useTranslation } from "react-i18next";
import { Home as HomeIcon, BookOpen, Settings as SettingsIcon } from "lucide-react";
import { NavLink } from "react-router-dom";
import { MiniPlayerBar } from "./components/MiniPlayerBar";
import InstallAppNudge from "./components/InstallAppNudge";
import { ParentGate } from "./components/ParentGate";
import { ParentGateProvider } from "./lib/parentGate";

import Login from "./pages/Login";
import LoginCode from "./pages/LoginCode";
import AuthCallback from "./pages/AuthCallback";
import AuthVerify from "./pages/AuthVerify";
import AuthError from "./pages/AuthError";
import Home from "./pages/Home";
import Catalogue from "./pages/Catalogue";
import PackDetail from "./pages/PackDetail";
import StoryDetail from "./pages/StoryDetail";
import Create from "./pages/Create";
import Generation from "./pages/Generation";
import Listen from "./pages/Listen";
import KidMode from "./pages/KidMode";
import Settings from "./pages/Settings";
import ChildAppearance from "./pages/ChildAppearance";
import MyStories from "./pages/MyStories";
import ChouettePlus from "./pages/ChouettePlus";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Legal from "./pages/Legal";
import PinReset from "./pages/PinReset";

function RequireAuth({ children }: { children: ReactElement }) {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  return children;
}

/**
 * `BrowserRouter` ne touche jamais au scroll : en revenant d'une fiche
 * scrollée vers le bas (« Retour » → `navigate(-1)`), la fenêtre gardait son
 * décalage alors que la page d'arrivée se re-rendait d'abord courte (état de
 * chargement pendant le fetch du catalogue). Sur la PWA iOS, WebKit bornait
 * mal ce scroll et laissait tout le haut de l'écran non repeint (rapporté le
 * 2026-09-27). On repart donc du haut à chaque changement de page, avant le
 * paint, et on coupe la restauration native du navigateur qui s'en mêlait
 * sur les retours arrière.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history) window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function DemoBadge() {
  const { t } = useTranslation();
  if (!isDemoMode()) return null;
  // `safe-top` : la pastille est `fixed top-0`, donc calée sous la barre de
  // statut en PWA installée sur iOS sans ce padding — voir index.html
  // (`viewport-fit=cover`) et les autres usages de `.safe-top`.
  return (
    <div className="safe-top fixed top-0 inset-x-0 z-40 flex justify-center pointer-events-none">
      <span className="mt-2 seed-tag !rotate-0 pointer-events-auto">
        <span className="seed-tag__text">{t("common.demoBadge")}</span>
      </span>
    </div>
  );
}

function BottomNav() {
  const { t } = useTranslation();
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    `tap-target flex flex-1 flex-col items-center justify-center gap-1 text-[11px] font-heading font-semibold ${
      isActive ? "text-sun" : "text-violet-muted"
    }`;
  return (
    <nav className="safe-bottom-nav fixed bottom-0 inset-x-0 z-30 bg-surface border-t border-line flex">
      <NavLink to="/" end className={linkClass}>
        <HomeIcon size={20} />
        {t("nav.home")}
      </NavLink>
      <NavLink to="/catalog" className={linkClass}>
        <BookOpen size={20} />
        {t("nav.catalog")}
      </NavLink>
      <NavLink to="/settings" className={linkClass}>
        <SettingsIcon size={20} />
        {t("nav.settings")}
      </NavLink>
    </nav>
  );
}

function Chrome({ children }: { children: ReactElement }) {
  return (
    <>
      {children}
      <MiniPlayerBar />
      <InstallAppNudge />
      <BottomNav />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ParentGateProvider>
      <ChildrenProvider>
        <AudioPlayerProvider>
          <ScrollToTop />
          <DemoBadge />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/login/code" element={<LoginCode />} />
            <Route path="/auth/verify" element={<AuthVerify />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/error" element={<AuthError />} />
            <Route path="/catalog" element={<Chrome><Catalogue /></Chrome>} />
            <Route path="/pack/:id" element={<Chrome><PackDetail /></Chrome>} />
            <Route path="/story/:id" element={<Chrome><StoryDetail /></Chrome>} />
            <Route path="/listen" element={<Listen />} />
            {/* Offre et pages légales : accessibles sans compte — un parent doit
                pouvoir lire les CGV ou la politique de confidentialité avant de
                s'inscrire, et la page Chouette+ sert aussi de vitrine. Elles
                restent donc hors de `RequireAuth` et hors de la barre
                d'onglets, dont toutes les destinations demandent un compte.
                /pricing est un alias de /plus : l'offre se cite des deux
                façons, autant que les deux adresses répondent. */}
            <Route path="/plus" element={<ParentGate><ChouettePlus /></ParentGate>} />
            <Route path="/pricing" element={<Navigate to="/plus" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/legal" element={<Legal />} />
            {/* Lien reçu par e-mail pour effacer le code parent : public, il
                s'ouvre souvent sur un appareil où le parent n'est pas connecté. */}
            <Route path="/pin-reset" element={<PinReset />} />
            <Route
              path="/"
              element={
                <RequireAuth>
                  <Chrome><Home /></Chrome>
                </RequireAuth>
              }
            />
            <Route
              path="/create"
              element={
                <RequireAuth>
                  <Chrome><ParentGate><Create /></ParentGate></Chrome>
                </RequireAuth>
              }
            />
            <Route
              path="/generation/:jobId"
              element={
                <RequireAuth>
                  <Generation />
                </RequireAuth>
              }
            />
            <Route
              path="/kid"
              element={
                <RequireAuth>
                  <KidMode />
                </RequireAuth>
              }
            />
            <Route
              path="/settings"
              element={
                <RequireAuth>
                  <Chrome><ParentGate><Settings /></ParentGate></Chrome>
                </RequireAuth>
              }
            />
            <Route
              path="/settings/children/:childId/appearance"
              element={
                <RequireAuth>
                  <Chrome><ParentGate><ChildAppearance /></ParentGate></Chrome>
                </RequireAuth>
              }
            />
            <Route
              path="/settings/stories"
              element={
                <RequireAuth>
                  <Chrome><ParentGate><MyStories /></ParentGate></Chrome>
                </RequireAuth>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AudioPlayerProvider>
      </ChildrenProvider>
      </ParentGateProvider>
    </AuthProvider>
  );
}
